import { connect } from 'react-redux';
import { isEmpty, last } from 'lodash/fp';

import { TARGET_SEGMENT } from '../../../cms/constants';
import { VARIANT, type VariantValue } from './constants';
import filterOffers from '../utils/filterOffers';
import MultiOfferDisplay from './components/multi-offer-display';
import selectReferralCode from '../../../selectors/select-referral-code';
import selectReferralUrl from '../../../selectors/select-referral-url';
import selectTargetSegment from '../../../selectors/select-target-segment';
import SingleOfferDisplay from './components/single-offer-display';
import sortOffersByProductType from './utils/sort-offers-by-product-type';
import useGetActivePromoOffer from '../logic/use-get-active-promo-offer';
import type { Offer, Placeholder } from '../types';

type OfferTileProps = {
  filterProductId?: string;
  heading?: string;
  subheading?: string;
  isBoxed?: boolean;
  overrideOffers?: Offer[];
  placeholders?: Placeholder[];
  referralCode?: string;
  referralUrl?: string;
  showCodeCopy?: boolean;
  showFindCover?: boolean;
  showMemberLogin?: boolean;
  targetSegment?: string;
  variant?: VariantValue;
};

const DisplayOffers = ({
  filterProductId,
  heading,
  subheading,
  isBoxed,
  offers,
  placeholders,
  referralCode,
  referralUrl,
  showCodeCopy,
  showFindCover,
  showMemberLogin,
  variant = VARIANT.TILE,
}: Partial<OfferTileProps> & { offers: Offer[] }) => {
  if (isEmpty(offers)) {
    return null;
  }

  if (offers.length > 1 && !filterProductId) {
    const sortedOffers = sortOffersByProductType(offers);

    return (
      <MultiOfferDisplay
        heading={heading}
        subheading={subheading}
        isBoxed={isBoxed}
        offers={sortedOffers}
        placeholders={placeholders}
        referralCode={referralCode}
        showFindCover={showFindCover}
        variant={variant}
      />
    );
  }

  return (
    <SingleOfferDisplay
      isBoxed={isBoxed}
      // @ts-expect-error - Automatic, Please fix when editing this file
      offer={last(offers)}
      placeholders={placeholders}
      // @ts-expect-error - Automatic, Please fix when editing this file
      referralCode={referralCode}
      referralUrl={referralUrl}
      // @ts-expect-error - Automatic, Please fix when editing this file
      showCodeCopy={showCodeCopy}
      // @ts-expect-error - Automatic, Please fix when editing this file
      showFindCover={showFindCover}
      // @ts-expect-error - Automatic, Please fix when editing this file
      showMemberLogin={showMemberLogin}
      variant={variant}
    />
  );
};

const OfferTile = ({
  filterProductId,
  heading,
  subheading,
  isBoxed = true,
  overrideOffers,
  placeholders,
  referralCode,
  referralUrl,
  showCodeCopy = true,
  showFindCover = true,
  showMemberLogin,
  targetSegment = TARGET_SEGMENT.LOOKING_FOR_PRIVATE_HEALTH_INSURANCE,
  variant = VARIANT.TILE,
}: OfferTileProps) => {
  const hasOverrideOffers = overrideOffers && overrideOffers.length > 0;
  const { activeOffers } = useGetActivePromoOffer({ skip: hasOverrideOffers, targetSegment });

  if (!hasOverrideOffers && isEmpty(activeOffers)) {
    return null;
  }

  if (hasOverrideOffers) {
    const displayOffers = filterProductId ? filterOffers({ filterProductId, offers: overrideOffers }) : overrideOffers;
    return (
      <DisplayOffers
        {...{
          filterProductId,
          heading,
          isBoxed,
          offers: displayOffers,
          placeholders,
          referralCode,
          referralUrl,
          showCodeCopy,
          showFindCover,
          showMemberLogin,
          subheading,
          variant,
        }}
      />
    );
  }

  const displayOffers = filterProductId ? filterOffers({ filterProductId, offers: activeOffers }) : activeOffers;

  return (
    <DisplayOffers
      {...{
        filterProductId,
        heading,
        isBoxed,
        offers: displayOffers,
        placeholders,
        referralCode,
        referralUrl,
        showCodeCopy,
        showFindCover,
        showMemberLogin,
        subheading,
        variant,
      }}
    />
  );
};

const mapStateToProps = (state: any) => ({
  referralCode: selectReferralCode(state) as unknown as string,
  referralUrl: selectReferralUrl(state) as unknown as string,
  targetSegment: selectTargetSegment(state),
});

export default connect(mapStateToProps)(OfferTile);
export { OfferTile as OfferTileForTest };
